import { useState } from "react";
import { rsvpApi } from "../services/api";
import { AuthResponse, PlusOne } from "../types";
import PlusOneModal from "./PlusOneModal";

interface EventViewProps {
  authData: AuthResponse;
  phone: string;
  accessCode: string;
  onRSVPComplete: () => void;
  onBackToSelection?: () => void;
  hasMultipleEvents?: boolean;
}

export function EventView({
  authData,
  phone,
  accessCode,
  onRSVPComplete,
  onBackToSelection,
  hasMultipleEvents = false,
}: EventViewProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPlusOneModal, setShowPlusOneModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);

  const handleRSVP = async (
    status: "GOING" | "GOING_WITH_GUESTS",
    plusOnes?: PlusOne[]
  ) => {
    setLoading(true);
    setError("");

    try {
      console.log("Updating RSVP with:", {
        eventId: authData.event.id,
        phone,
        accessCode,
        rsvpStatus: status,
        plusOnes,
      });
      const response = await rsvpApi.updateRSVP({
        eventId: authData.event.id,
        phone,
        accessCode,
        rsvpStatus: status,
        plusOnes,
      });
      console.log("RSVP update successful:", response);

      onRSVPComplete();
    } catch (err: any) {
      console.error("RSVP update failed:", err);
      console.error("Error response:", err.response?.data);
      setError(err.response?.data?.error || "Failed to update RSVP");
    } finally {
      setLoading(false);
    }
  };

  const handlePlusOneConfirm = (plusOnes: PlusOne[]) => {
    setShowPlusOneModal(false);
    handleRSVP("GOING_WITH_GUESTS", plusOnes);
  };

  const handleYesClick = () => {
    setShowGuestModal(true);
  };

  const handleNoGuests = () => {
    setShowGuestModal(false);
    handleRSVP("GOING");
  };

  const handleYesGuests = () => {
    setShowGuestModal(false);
    setShowPlusOneModal(true);
  };

  return (
    <div
      className={`minimalist-event-page ${
        !authData.event.posterUrl ? "no-poster" : ""
      }`}
      style={{
        minHeight: "100dvh", // Use dynamic viewport height for mobile
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100dvh",
        overflow: "auto", // Allow scrolling to see full poster
        zIndex: 1,
      }}
    >
      {/* Event Poster - Dominates the page */}
      {authData.event.posterUrl ? (
        <div className="poster-hero">
          <img
            src={authData.event.posterUrl}
            alt="Event Poster"
            className="hero-poster"
            // Removed inline styles to let CSS media queries control display
          />
        </div>
      ) : (
        <div className="poster-hero"></div>
      )}

      {/* Back Button - Fixed at top of page, outside overlay */}
      {hasMultipleEvents && onBackToSelection && (
        <button
          className="back-to-events-button"
          onClick={onBackToSelection}
          style={{
            position: "fixed",
            top: "1.25rem",
            left: "1.25rem",
            background: "rgba(255, 255, 255, 0.75)", /* Enhanced glassmorphic */
            backdropFilter: "blur(25px) saturate(140%)",
            WebkitBackdropFilter: "blur(25px) saturate(140%)",
            border: "1px solid rgba(255, 255, 255, 0.4)",
            borderRadius: "12px",
            padding: "0.75rem 1rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.625rem",
            fontSize: "0.875rem",
            fontWeight: "600",
            color: "#374151",
            boxShadow:
              "0 8px 25px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.3) inset",
            zIndex: 100,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 1)";
            e.currentTarget.style.transform = "translateX(-2px)";
            e.currentTarget.style.boxShadow =
              "0 12px 30px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
            e.currentTarget.style.transform = "translateX(0px)";
            e.currentTarget.style.boxShadow =
              "0 8px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)";
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 12H5M12 19l-7-7 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to Events
        </button>
      )}

      {/* Event Details - Minimal overlay */}
      <div className="event-details-overlay">
        {/* Only show event details if there's NO poster - all info should be on the poster */}
        {!authData.event.posterUrl && (
          <>
            <h1 className="event-title">{authData.event.name}</h1>

            {/* Event Description */}
            {authData.event.description && (
              <div className="event-description">
                <p
                  style={{
                    fontSize: "0.9rem",
                    lineHeight: "1.4",
                    marginBottom: "0.75rem",
                    textShadow: "0 1px 3px rgba(0, 0, 0, 0.8)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {authData.event.description}
                </p>
              </div>
            )}

            {/* Event Info */}
            <div className="event-info">
              {authData.event.date && (
                <div className="info-item">
                  <span className="info-label">📅</span>
                  <span className="info-text">
                    {new Date(authData.event.date).toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                      timeZone: 'UTC'
                    })}
                  </span>
                </div>
              )}

              {authData.event.time && (
                <div className="info-item">
                  <span className="info-label">🕐</span>
                  <span className="info-text">{authData.event.time}</span>
                </div>
              )}

              {authData.event.location && (
                <div className="info-item">
                  <span className="info-label">📍</span>
                  <span className="info-text">{authData.event.location}</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* RSVP Section */}
        <div className="rsvp-section">
          <p className="rsvp-greeting">
            Can you make it?
          </p>

          {error && <div className="error-message">{error}</div>}

          <div className="rsvp-buttons">
            <button
              className="rsvp-btn rsvp-yes"
              onClick={handleYesClick}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Updating...
                </>
              ) : (
                "✅ Yes, I'll be there!"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Guest Confirmation Modal */}
      {showGuestModal && (
        <div className="modal-overlay">
          <div className="guest-confirmation-modal">
            <div className="guest-confirmation-header">
              <div className="guest-confirmation-title">
                <div className="guest-confirmation-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="9"
                      cy="7"
                      r="4"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>Bringing Guests?</h3>
              </div>
            </div>

            <div className="guest-confirmation-body">
              <p className="guest-confirmation-question">
                Would you like to bring any guests with you to this event?
              </p>
            </div>

            <div className="guest-confirmation-footer">
              <button
                className="guest-confirmation-cancel-btn"
                onClick={handleNoGuests}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="9"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                Just Me
              </button>
              <button
                className="guest-confirmation-primary-btn"
                onClick={handleYesGuests}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="9"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Yes, I'm Bringing Guests
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plus One Modal */}
      <PlusOneModal
        isOpen={showPlusOneModal}
        onClose={() => setShowPlusOneModal(false)}
        onConfirm={handlePlusOneConfirm}
      />
    </div>
  );
}
